import { useTranslate } from '@tolgee/react';
import { MenuSource } from 'core/src/graphql/types/graphql';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PrimaryButton from 'ui/src/button/primary-button';
import SecondaryButton from 'ui/src/button/secondary-button';
import Card from 'ui/src/card/card';
import CardContent from 'ui/src/card/card-content';
import CardFooter from 'ui/src/card/card-footer';
import CardHeader from 'ui/src/card/card-header';
import FormItem from 'ui/src/form/form-item';
import { useParitalCopyMenu } from '../logic/use-partial-copy-menu';

enum MonitorState {
  form = 0,
  success = 1,
  error = 2,
}

interface CopyMenuForm {
  title: string;
  menuSource: MenuSource;
}

export default function CopyMenu({
  onClose,
  baseMenuId,
  productIds,
  defaultMenuSource,
}: {
  onClose: () => void;
  baseMenuId: string;
  productIds: string[];
  defaultMenuSource: MenuSource;
}) {
  const { t } = useTranslate();
  const [monitorState, setMonitorState] = useState<MonitorState>(MonitorState.form);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CopyMenuForm>({
    defaultValues: {
      title: '',
      menuSource: defaultMenuSource,
    },
  });

  const [copyMenu, { loading }] = useParitalCopyMenu();

  // Update default menu source when props change
  useEffect(() => {
    reset({ menuSource: defaultMenuSource, title: '' });
  }, [defaultMenuSource, reset]);

  // Form submit handler
  const onSubmit = async (data: CopyMenuForm) => {
    try {
      await copyMenu({
        variables: {
          baseMenuId,
          productIds,
          data: {
            title: data.title,
            menuSource: data.menuSource,
          },
        },
      });

      setMonitorState(MonitorState.success);
    } catch (e) {
      setMonitorState(MonitorState.error);
    }
  };

  return (
    <Card>
      <CardHeader title={t('menu.copy.title', 'Copy Menu')} />

      <CardContent>
        {monitorState === MonitorState.form && (
          <FormItem title={t('create-form.form-input.menu-title.title', 'Title')}>
            <input
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1
                             ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 "
              type="text"
              {...register('title', { required: t('menu.copy.menu_title_required', 'Title is required') })}
            />
            {errors.title && <p className="text-red-500 text-xs pt-1">{errors.title.message}</p>}
          </FormItem>
        )}

        {monitorState === MonitorState.success && (
          <div className="space-y-4 text-center">
            <h2 className="text-xl font-semibold text-green-600">
              {t('menu.copy.success_title', 'Copied Successfully')}
            </h2>
            <p className="text-sm text-gray-700">
              {t(
                'menu.copy.success_message',
                'The menu has been successfully copied. Do you want to navigate to new menu'
              )}
            </p>
          </div>
        )}

        {monitorState === MonitorState.error && (
          <div className="space-y-4 text-center">
            <h2 className="text-xl font-semibold text-red-600">{t('menu.copy.error_title', 'Copy Failed')}</h2>
            <p className="text-sm text-gray-700">
              {t('menu.copy.error_message', 'An error occurred while copying the menu. Please try again later.')}
            </p>
          </div>
        )}
      </CardContent>

      <CardFooter>
        {monitorState === MonitorState.form && (
          <div className="flex justify-between w-full space-x-2">
            <SecondaryButton onClick={onClose}>{t('common.action.close', 'Close')}</SecondaryButton>
            <PrimaryButton
              onClick={() => {
                void handleSubmit(onSubmit)();
              }}
              loading={loading}
            >
              {t('menu.copy.submit', 'Copy Menu')}
            </PrimaryButton>
          </div>
        )}

        {monitorState === MonitorState.success && (
          <div className="flex items-center justify-between gap-x-4">
            <PrimaryButton onClick={onClose}>{t('common.action.close', 'Close')}</PrimaryButton>
          </div>
        )}

        {monitorState === MonitorState.error && (
          <div className="flex justify-end">
            <PrimaryButton onClick={onClose}>{t('common.action.close', 'Close')}</PrimaryButton>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
